import { forwardRef, type Ref } from 'react';
import { TextInput, type TextInputProps } from '../TextInput/TextInput';

export interface SearchInputProps extends Omit<TextInputProps, 'icon' | 'type'> {
  /** Render a "⌘K" hint on the trailing edge (web ⌘+K to focus). */
  readonly shortcutHint?: boolean;
}

/**
 * Search input — TextInput preset with a leading search icon and optional
 * ⌘K shortcut hint. The shortcut binding itself lives at the app level
 * (the focus handler decides which input gets selected when ⌘K fires).
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { shortcutHint = false, trailing, placeholder = 'Search', ...rest }: SearchInputProps,
  ref: Ref<HTMLInputElement>,
) {
  const trailingNode =
    trailing ??
    (shortcutHint ? (
      <kbd className="font-mono text-[10px] text-ink-3 bg-surface-2 border border-hairline-2 rounded px-1 py-0.5">
        ⌘K
      </kbd>
    ) : undefined);
  return (
    <TextInput
      ref={ref}
      type="search"
      icon="search"
      placeholder={placeholder}
      trailing={trailingNode}
      {...rest}
    />
  );
});
